import {
  Sparkles, Target, Trophy, BarChart3, ShoppingBag, Users, Bot, Layers,
  Cross, KeyRound, Grid3x3, Brain, Hash, PawPrint, Repeat, Star,
  Infinity as InfinityIcon, Dices, ListOrdered, Calculator, Search,
  FileSpreadsheet, Smartphone, type LucideIcon,
} from "lucide-react";

export type VitrineCategory =
  | "metodos"
  | "ia"
  | "estatisticas"
  | "resultados"
  | "comunidade"
  | "loja"
  | "afiliados";

export type VitrineBadge = "NOVO" | "PREMIUM" | "IA" | "GRÁTIS";

export interface VitrineItem {
  id: string;
  title: string;
  tagline: string;
  description: string;
  category: VitrineCategory;
  badges: VitrineBadge[];
  icon: LucideIcon;
  /** Tailwind gradient classes for the card cover (used as image fallback). */
  gradient: string;
  /** Destination route (logged-in users go straight to the tool). */
  route: string;
  /** Premium-only feature → free users see CTA "Ver planos" too. */
  premium?: boolean;
  steps: string[];
  idealPara: string;
}

export const vitrineItems: VitrineItem[] = [
  // ===== Lançamento mais recente =====
  {
    id: "guru-chat",
    title: "Guru Chat — IA conversacional",
    tagline: "Converse com a IA: sonhos, palpites do dia e ferramentas com 1 clique.",
    description:
      "Novo chat com IA do IA Palpite Guru. Conte um sonho, peça palpite do dia ou diga como podemos ajudar. A IA sugere a melhor ferramenta (Gerar com IA, Estatísticas, Métodos...) e gera palpites prontos para compartilhar no WhatsApp ou baixar como imagem.",
    category: "ia",
    badges: ["NOVO", "IA"],
    icon: Bot,
    gradient: "from-amber-500 via-yellow-500 to-gold-500",
    route: "/guru-chat",
    steps: [
      "Abra o Guru Chat",
      "Conte um sonho ou peça um palpite",
      "A IA responde e sugere a ferramenta ideal",
      "Compartilhe ou baixe o palpite",
    ],
    idealPara: "Quem quer uma ajuda inteligente, rápida e conversacional dentro do app.",
  },
  // ===== Métodos =====
  {
    id: "centena-matadora",
    title: "Centena Matadora",
    tagline: "Desdobramento progressivo com 4 níveis (4/10/20/30).",
    description:
      "Gera centenas com base em desdobramento estatístico em 4 níveis de cobertura. Ideal para quem quer aumentar a chance de acerto sem inflar o custo.",
    category: "metodos",
    badges: ["NOVO", "PREMIUM"],
    icon: Target,
    gradient: "from-red-500 via-orange-500 to-amber-500",
    route: "/metodos?method=centena-matadora",
    premium: true,
    steps: [
      "Acesse o método na página Métodos",
      "Escolha a quantidade (4, 10, 20 ou 30 centenas)",
      "Gere e salve seus jogos (válidos por 7 dias)",
      "Compartilhe ou confira após o resultado",
    ],
    idealPara: "Apostadores que querem desdobramento inteligente sem complicação.",
  },
  {
    id: "desdobramento-jb",
    title: "Desdobramento JB",
    tagline: "Cobertura ampla com poucos jogos.",
    description: "Desdobramento clássico do Jogo do Bicho com cálculo automático de combinações.",
    category: "metodos",
    badges: ["PREMIUM"],
    icon: Layers,
    gradient: "from-sky-500 via-blue-500 to-indigo-500",
    route: "/metodos?method=desdobramento-jb",
    premium: true,
    steps: ["Selecione dezenas base", "Defina cobertura", "Gere combinações", "Aposte"],
    idealPara: "Quem aposta em vários grupos com pouco capital.",
  },
  {
    id: "sistema-ia-bicho",
    title: "Sistema IA Bicho",
    tagline: "Predição com IA baseada em tendências de repetição.",
    description: "Inteligência artificial analisa o histórico (PPT a COR) para indicar grupos com maior tendência.",
    category: "ia",
    badges: ["IA", "PREMIUM"],
    icon: Bot,
    gradient: "from-fuchsia-500 via-purple-500 to-violet-600",
    route: "/metodos?method=sistema-ia-bicho",
    premium: true,
    steps: ["Insira os últimos resultados", "A IA processa as tendências", "Receba os grupos sugeridos"],
    idealPara: "Quem confia em análise de dados e padrões.",
  },
  {
    id: "cruz-dia",
    title: "Cruz do Dia",
    tagline: "Método tradicional reinventado.",
    description: "Calcula a cruz baseada em resultados anteriores para indicar centenas fortes.",
    category: "metodos",
    badges: ["PREMIUM"],
    icon: Cross,
    gradient: "from-red-500 via-rose-500 to-pink-500",
    route: "/metodos?method=cruz-dia",
    premium: true,
    steps: ["Informe a centena base", "Veja a cruz completa", "Aposte nas centenas geradas"],
    idealPara: "Quem gosta dos métodos tradicionais com automação.",
  },
  {
    id: "esquema-infalivel",
    title: "Esquema Infalível",
    tagline: "Esquema fechado de cobertura ampla.",
    description: "Estrutura matemática que cobre múltiplos cenários do sorteio.",
    category: "metodos",
    badges: ["PREMIUM"],
    icon: InfinityIcon,
    gradient: "from-rose-500 via-red-500 to-orange-500",
    route: "/metodos?method=esquema-infalivel",
    premium: true,
    steps: ["Defina os palpites", "Gere o esquema", "Confira os resultados"],
    idealPara: "Apostadores experientes que querem cobertura máxima.",
  },
  {
    id: "dezenas-premiadas",
    title: "Dezenas Premiadas",
    tagline: "As dezenas que mais saíram nos últimos sorteios.",
    description: "Lista atualizada das dezenas com maior frequência recente.",
    category: "metodos",
    badges: ["PREMIUM"],
    icon: Star,
    gradient: "from-yellow-500 via-amber-500 to-orange-400",
    route: "/metodos?method=dezenas-premiadas",
    premium: true,
    steps: ["Abra o método", "Veja o ranking de dezenas", "Selecione as suas favoritas"],
    idealPara: "Quem aposta em dezenas com base em frequência.",
  },
  {
    id: "metodo-777",
    title: "Método 777",
    tagline: "Esquema com 7 grupos selecionados.",
    description: "Combinação de 7 grupos selecionados estrategicamente.",
    category: "metodos",
    badges: ["PREMIUM"],
    icon: Dices,
    gradient: "from-violet-500 via-purple-500 to-indigo-500",
    route: "/metodos?method=metodo-777",
    premium: true,
    steps: ["Acesse o método", "Veja a sugestão de 7 grupos", "Aposte com confiança"],
    idealPara: "Apostadores que jogam em grupos.",
  },
  {
    id: "metodo-sequencia",
    title: "Método Sequência",
    tagline: "Sequência de números com lógica matemática.",
    description: "Geração de sequências baseada em padrões matemáticos do sorteio.",
    category: "metodos",
    badges: ["PREMIUM"],
    icon: ListOrdered,
    gradient: "from-teal-500 via-cyan-500 to-sky-500",
    route: "/metodos?method=metodo-sequencia",
    premium: true,
    steps: ["Escolha base inicial", "Gere a sequência", "Aposte"],
    idealPara: "Quem gosta de jogar em sequências numéricas.",
  },
  {
    id: "centena-bichos",
    title: "Centena dos Bichos",
    tagline: "Centenas vinculadas a cada animal.",
    description: "Mostra todas as centenas associadas aos 25 grupos.",
    category: "metodos",
    badges: ["PREMIUM"],
    icon: PawPrint,
    gradient: "from-emerald-500 via-green-500 to-lime-500",
    route: "/metodos?method=centena-bichos",
    premium: true,
    steps: ["Selecione o bicho", "Veja todas as centenas", "Escolha as suas"],
    idealPara: "Apostadores que jogam em centenas por bicho.",
  },
  {
    id: "7-grupos",
    title: "7 Grupos",
    tagline: "Os 7 grupos mais quentes do momento.",
    description: "Análise estatística dos 7 grupos com maior probabilidade.",
    category: "metodos",
    badges: ["PREMIUM"],
    icon: Grid3x3,
    gradient: "from-purple-500 via-violet-500 to-fuchsia-500",
    route: "/metodos?method=7-grupos",
    premium: true,
    steps: ["Acesse o método", "Veja os 7 grupos sugeridos", "Aposte"],
    idealPara: "Quem aposta em grupos do bicho.",
  },
  {
    id: "centenas-viciadas",
    title: "Centenas Viciadas",
    tagline: "As centenas que mais se repetem.",
    description: "Lista das centenas com maior recorrência histórica.",
    category: "metodos",
    badges: ["PREMIUM"],
    icon: Repeat,
    gradient: "from-cyan-500 via-blue-500 to-indigo-500",
    route: "/metodos?method=centenas-viciadas",
    premium: true,
    steps: ["Abra o método", "Veja o ranking", "Aposte nas top centenas"],
    idealPara: "Quem aposta em centenas frequentes.",
  },
  {
    id: "bionumerico-stellar",
    title: "BioNumérico Stellar",
    tagline: "Numerologia + dados pessoais para palpite único.",
    description: "Combina sua data de nascimento e dados pessoais para gerar palpites personalizados.",
    category: "metodos",
    badges: ["PREMIUM"],
    icon: Brain,
    gradient: "from-pink-500 via-rose-500 to-fuchsia-500",
    route: "/metodos?method=bionumerico-stellar",
    premium: true,
    steps: ["Informe seus dados", "A análise é gerada", "Receba seu palpite pessoal"],
    idealPara: "Quem acredita em numerologia e palpite pessoal.",
  },
  {
    id: "numerologia-nome",
    title: "Numerologia do Nome",
    tagline: "Seu nome, seu palpite.",
    description: "Calcula a vibração numérica do seu nome para gerar palpites.",
    category: "metodos",
    badges: ["PREMIUM"],
    icon: Hash,
    gradient: "from-indigo-500 via-purple-500 to-pink-500",
    route: "/metodos?method=numerologia-nome",
    premium: true,
    steps: ["Digite seu nome", "Receba os números", "Aposte"],
    idealPara: "Quem busca palpite místico e personalizado.",
  },
  {
    id: "soma-chave-ouro",
    title: "Soma Chave de Ouro",
    tagline: "Cálculo com base na chave 0663.",
    description: "Aplica a chave de ouro 0663 sobre os resultados anteriores para gerar centenas.",
    category: "metodos",
    badges: ["PREMIUM"],
    icon: Calculator,
    gradient: "from-orange-500 via-amber-500 to-yellow-500",
    route: "/metodos?method=soma-chave-ouro",
    premium: true,
    steps: ["Informe o último resultado", "Aplique a chave", "Veja as centenas geradas"],
    idealPara: "Quem usa métodos baseados em chaves matemáticas.",
  },
  {
    id: "calculadora-chave-ouro",
    title: "Calculadora Chave de Ouro",
    tagline: "Chaves regionais (Rio 469, SP 385).",
    description: "Calculadora com chaves regionais e 4 operações aritméticas.",
    category: "metodos",
    badges: ["PREMIUM"],
    icon: KeyRound,
    gradient: "from-amber-500 via-yellow-500 to-orange-500",
    route: "/metodos?method=calculadora-chave-ouro",
    premium: true,
    steps: ["Escolha a chave (RJ/SP)", "Selecione a operação", "Gere o palpite"],
    idealPara: "Apostadores regionais (RJ ou SP).",
  },
  {
    id: "achar-centena-dia",
    title: "Achar Centena do Dia",
    tagline: "Descubra a centena forte do dia.",
    description: "Algoritmo identifica a centena com maior probabilidade no dia.",
    category: "metodos",
    badges: ["PREMIUM"],
    icon: Search,
    gradient: "from-lime-500 via-green-500 to-emerald-500",
    route: "/metodos?method=achar-centena-dia",
    premium: true,
    steps: ["Acesse o método", "Veja a centena do dia", "Aposte"],
    idealPara: "Quem aposta diariamente.",
  },

  // ===== IA & Análise =====
  {
    id: "gerar-jogos-ia",
    title: "Gerador de Palpites IA",
    tagline: "Palpites gerados por inteligência artificial.",
    description: "Nosso motor de IA analisa milhares de combinações e entrega palpites otimizados.",
    category: "ia",
    badges: ["IA", "PREMIUM"],
    icon: Sparkles,
    gradient: "from-yellow-500 via-amber-500 to-orange-500",
    route: "/gerar-jogos",
    premium: true,
    steps: ["Escolha a loteria", "Configure parâmetros", "A IA gera os palpites"],
    idealPara: "Quem quer praticidade e tecnologia em um só lugar.",
  },
  {
    id: "software-turbinado",
    title: "Software Turbinado IA",
    tagline: "Plataforma completa com IA para gestão dos seus jogos.",
    description: "Sistema completo para gestão de apostas com IA integrada.",
    category: "ia",
    badges: ["IA", "PREMIUM"],
    icon: FileSpreadsheet,
    gradient: "from-blue-500 via-indigo-500 to-purple-500",
    route: "/software",
    premium: true,
    steps: ["Configure seu perfil", "Cadastre apostas", "Acompanhe métricas"],
    idealPara: "Apostadores frequentes que querem controle total.",
  },

  // ===== Estatísticas =====
  {
    id: "estatisticas",
    title: "Estatísticas Avançadas",
    tagline: "Frequência de centenas, dezenas, milhares e grupos.",
    description: "Painel completo com gráficos e rankings em tempo real para todas as faixas.",
    category: "estatisticas",
    badges: ["PREMIUM"],
    icon: BarChart3,
    gradient: "from-yellow-600 via-amber-600 to-yellow-500",
    route: "/estatisticas",
    premium: true,
    steps: ["Acesse o painel", "Escolha a faixa de análise", "Veja os rankings e gere jogos"],
    idealPara: "Quem aposta com base em dados reais.",
  },

  // ===== Resultados =====
  {
    id: "resultados-bicho",
    title: "Resultados ao Vivo",
    tagline: "Resultados do Jogo do Bicho atualizados.",
    description: "Resultados de RIO, SP, BA e mais — atualizados ao vivo.",
    category: "resultados",
    badges: ["GRÁTIS"],
    icon: Trophy,
    gradient: "from-amber-500 via-orange-500 to-red-500",
    route: "/resultados-bicho",
    steps: ["Acesse a página", "Veja o resultado da banca desejada"],
    idealPara: "Todos os apostadores.",
  },
  {
    id: "loterias",
    title: "Loterias Caixa",
    tagline: "Mega-Sena, Lotofácil, Quina e mais.",
    description: "Geração de jogos para todas as loterias da Caixa com fechamentos.",
    category: "resultados",
    badges: ["PREMIUM"],
    icon: Trophy,
    gradient: "from-emerald-500 via-green-500 to-teal-500",
    route: "/loterias",
    premium: true,
    steps: ["Escolha a loteria", "Configure o jogo", "Gere e aposte"],
    idealPara: "Quem joga nas loterias da Caixa.",
  },

  // ===== Comunidade =====
  {
    id: "puxadas",
    title: "Puxadas",
    tagline: "Puxadas dos jogos compartilhadas.",
    description: "Sistema de puxadas baseado em sorteios anteriores.",
    category: "comunidade",
    badges: ["PREMIUM"],
    icon: PawPrint,
    gradient: "from-orange-500 via-amber-500 to-yellow-500",
    route: "/puxadas",
    premium: true,
    steps: ["Acesse Puxadas", "Veja as combinações", "Compartilhe"],
    idealPara: "Quem aposta com puxadas.",
  },

  // ===== Loja =====
  {
    id: "loja",
    title: "Loja & Produtos",
    tagline: "Planilhas, cursos, ebooks e ferramentas.",
    description: "Produtos selecionados para potencializar suas apostas.",
    category: "loja",
    badges: ["GRÁTIS"],
    icon: ShoppingBag,
    gradient: "from-pink-500 via-rose-500 to-red-500",
    route: "/loja",
    steps: ["Navegue por categoria", "Escolha o produto", "Compre direto na loja parceira"],
    idealPara: "Quem busca conteúdo extra para apostar melhor.",
  },

  // ===== Afiliados =====
  {
    id: "afiliados",
    title: "Programa de Afiliados",
    tagline: "Indique amigos e ganhe comissão.",
    description: "Indique o IA Palpite Guru e receba comissão por cada assinatura.",
    category: "afiliados",
    badges: ["GRÁTIS"],
    icon: Users,
    gradient: "from-green-500 via-emerald-500 to-teal-500",
    route: "/afiliados",
    steps: ["Acesse o painel de afiliados", "Copie seu link", "Compartilhe e ganhe"],
    idealPara: "Quem quer monetizar sua audiência.",
  },
  {
    id: "instalar",
    title: "Instalar App (PWA)",
    tagline: "Tenha o IA Palpite Guru no seu celular.",
    description: "Instale o app no seu celular ou desktop com 1 clique.",
    category: "comunidade",
    badges: ["GRÁTIS"],
    icon: Smartphone,
    gradient: "from-sky-500 via-cyan-500 to-blue-500",
    route: "/instalar",
    steps: ["Acesse a página de instalação", "Clique em Instalar", "Use offline"],
    idealPara: "Todos que usam pelo celular.",
  },
];

export const CATEGORIES: { id: VitrineCategory | "all"; label: string }[] = [
  { id: "all", label: "Todos" },
  { id: "metodos", label: "Métodos" },
  { id: "ia", label: "IA & Análise" },
  { id: "estatisticas", label: "Estatísticas" },
  { id: "resultados", label: "Resultados" },
  { id: "comunidade", label: "Comunidade" },
  { id: "loja", label: "Loja" },
  { id: "afiliados", label: "Afiliados" },
];
